/**
 * 全站表格：固定列（fixed="left/right"）与主表对齐。
 * 1. 外框：按表体真实滚动条宽高重算 right / 高度，避免和表头、横向滚动条错位。
 *    机构管理页能对齐，是因为滚动条只有样式里的 6px，和 Element 的 gutterWidth 一致。
 * 2. 行高：固定列是叠在主表上的独立表格，主表换行变高时操作按钮会错位。逐行取最大高度统一。
 * 表格根节点加 class「no-fixed-row-sync」可关闭。
 * 创建于 2026-09-30；最后修改：2026-10-14
 */

const ROW_SELECTOR = '.el-table__body > tbody > tr'
const SCHEDULE_DELAY = 60

/** 清掉上次写在行上的高度，才能量到内容自然高度。只动 tr：td 是 content-box，写成边框盒高度会把行撑高一圈 */
function clearRowHeight(tr) {
  if (tr.style.height) tr.style.height = ''
}

function applyRowHeight(tr, heightPx) {
  tr.style.height = `${heightPx}px`
}

/**
 * 右固定列贴齐真实滚动条：right、补丁宽度、列高跟表体走，不跟 Element 缓存的 gutterWidth。
 * 手机紧凑布局由样式把高度放开，这里不再写死像素。
 * @param {Vue} table ElTable 实例
 */
function syncFixedColumnFrame(table) {
  const el = table.$el
  const app = document.getElementById('app')
  if (!el || (app && app.classList.contains('is-compact-layout'))) return
  const refs = table.$refs || {}
  const bodyWrapper = refs.bodyWrapper
  const header = refs.headerWrapper
  const rightFixed = refs.rightFixedWrapper
  if (!bodyWrapper || !header || !rightFixed) return

  const tableRect = el.getBoundingClientRect()
  const bodyRect = bodyWrapper.getBoundingClientRect()
  if (!tableRect.height || !bodyRect.height) return

  const yGutter = Math.max(0, bodyWrapper.offsetWidth - bodyWrapper.clientWidth)
  const xGutter = Math.max(0, bodyWrapper.offsetHeight - bodyWrapper.clientHeight)
  const fixedHeight = Math.max(0, Math.round(bodyRect.bottom - tableRect.top - xGutter))
  const bodyTop = Math.max(0, Math.round(bodyRect.top - tableRect.top))
  const bodyHeight = Math.max(0, Math.round(bodyRect.height - xGutter))

  rightFixed.style.right = `${yGutter}px`
  rightFixed.style.height = `${fixedHeight}px`

  const patch = el.querySelector('.el-table__fixed-right-patch')
  if (patch) {
    patch.style.width = `${yGutter}px`
    patch.style.height = `${bodyTop}px`
  }

  const rightBody = refs.rightFixedBodyWrapper
  if (rightBody) {
    rightBody.style.top = `${bodyTop}px`
    rightBody.style.height = `${bodyHeight}px`
  }

  const leftFixed = refs.fixedWrapper
  if (leftFixed) leftFixed.style.height = `${fixedHeight}px`
  const leftBody = refs.fixedBodyWrapper
  if (leftBody) {
    leftBody.style.top = `${bodyTop}px`
    leftBody.style.height = `${bodyHeight}px`
  }
}

/**
 * 逐行对齐主表与固定列行高：先清掉上次写入的高度，批量读取自然高度，再批量写入最大值
 * @param {Vue} table ElTable 实例
 */
function syncFixedRowHeights(table) {
  const el = table.$el
  const refs = table.$refs || {}
  const fixedBodies = [refs.fixedBodyWrapper, refs.rightFixedBodyWrapper].filter(Boolean)
  if (!el || !el.offsetWidth || !refs.bodyWrapper || !fixedBodies.length) return
  if (el.classList.contains('no-fixed-row-sync')) return

  const groups = [refs.bodyWrapper].concat(fixedBodies)
    .map((wrapper) => Array.prototype.slice.call(wrapper.querySelectorAll(ROW_SELECTOR)))
  const rowCount = groups[0].length
  if (rowCount && groups.every((rows) => rows.length === rowCount)) {
    groups.forEach((rows) => rows.forEach(clearRowHeight))

    const natural = groups.map((rows) => rows.map((tr) => tr.getBoundingClientRect().height))
    for (let i = 0; i < rowCount; i++) {
      const target = Math.max.apply(null, natural.map((heights) => heights[i]))
      groups.forEach((rows, g) => {
        if (target - natural[g][i] > 0.5) applyRowHeight(rows[i], target)
      })
    }
  }

  // 行高落定后再贴滚动条，避免先量到的滚动条宽高过期
  syncFixedColumnFrame(table)
}

const fixedRowSyncMixin = {
  created() {
    const rawResizeListener = this.resizeListener
    if (typeof rawResizeListener === 'function') {
      this.resizeListener = (...args) => {
        rawResizeListener.apply(this, args)
        this.scheduleFixedRowSync()
      }
    }
  },
  mounted() {
    const rawDoLayout = this.doLayout
    if (typeof rawDoLayout === 'function') {
      this.doLayout = (...args) => {
        const result = rawDoLayout.apply(this, args)
        this.scheduleFixedRowSync()
        return result
      }
    }
    this.scheduleFixedRowSync()
    if (typeof window.ResizeObserver === 'function' && this.$refs.bodyWrapper) {
      const body = this.$refs.bodyWrapper.querySelector('.el-table__body')
      if (body) {
        this.fixedRowSyncObserver = new window.ResizeObserver(() => this.scheduleFixedRowSync())
        this.fixedRowSyncObserver.observe(body)
      }
    }
  },
  watch: {
    data() {
      this.scheduleFixedRowSync()
    }
  },
  beforeDestroy() {
    clearTimeout(this.fixedRowSyncTimer)
    if (this.fixedRowSyncObserver) {
      this.fixedRowSyncObserver.disconnect()
      this.fixedRowSyncObserver = null
    }
  },
  methods: {
    /** 等渲染完成后再对齐，合并短时间内的多次触发 */
    scheduleFixedRowSync() {
      clearTimeout(this.fixedRowSyncTimer)
      this.fixedRowSyncTimer = setTimeout(() => {
        this.$nextTick(() => {
          try {
            syncFixedRowHeights(this)
          } catch (e) {
            /* 对齐失败不影响表格正常使用 */
          }
        })
      }, SCHEDULE_DELAY)
    }
  }
}

/**
 * 为 ElTable 注入固定列行高同步能力，须在 Vue.use(ElementUI) 之前调用
 * @param {*} ElementUI
 */
export function installTableFixedRowSync(ElementUI) {
  const Table = ElementUI && ElementUI.Table
  if (!Table) return
  Table.mixins = (Table.mixins || []).concat(fixedRowSyncMixin)
}
